import styled from "@emotion/styled"
import { PButton, PHeading } from "@porsche-design-system/components-react"
import { colorContrastLow, spacingStaticLg } from "@porsche-design-system/components-react/emotion"
import type { BasicPricingValues, InventoryInformationChange } from "@slfinrtl/ui-bff-routes"
import { useCallback, useContext, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { defineMessages, FormattedMessage } from "react-intl"
import { formMessages } from "src/common/form.messages"
import type { InventoryInformationFormValues } from "src/components"
import { ButtonGroup, mapToInventoryInformationChange } from "src/components"
import { Modal } from "src/context/modal/Modal"
import { ModalContext } from "src/context/modal/ModalContext"
import type { ModalProps } from "src/context/modal/ModalProvider"
import { ConditionAndServiceChanges } from "./components/conditionAndServiceChanges/ConditionAndServiceChanges"
import type { ConditionAndServiceChangeView } from "./components/conditionAndServiceChanges/ConditionAndServiceChanges"
import { ConditionChangeForm } from "./components/conditionChangeForm/ConditionChangeForm"
import type { InventoryAndIntakeChangeView } from "./components/inventoryAndIntakeChanges/InventoryAndIntakeChanges"
import { InventoryAndIntakeChanges } from "./components/inventoryAndIntakeChanges/InventoryAndIntakeChanges"
import { ListingChangeForm } from "./components/listingChangeForm/ListingChangeForm"
import { PricingChanges } from "./components/pricingChanges/PricingChanges"

const messages = defineMessages({
	changesHeadline: {
		defaultMessage: "You are about to change",
		id: "vehicleDetails.overview.inventory.updateModal.changes.headline",
	},
	conditionHeadline: {
		defaultMessage: "This leads to changes in State & History",
		id: "vehicleDetails.overview.inventory.updateModal.condition.headline",
	},
	listingHeadline: {
		defaultMessage: "This leads to changes in the listing",
		id: "vehicleDetails.overview.inventory.updateModal.listing.headline",
	},
	saveChanges: {
		defaultMessage: "Save changes",
		id: "vehicleDetails.overview.inventory.updateModal.saveChanges",
	},
})

export interface InventoryAndIntakeUpdateModalProps {
	conditionAndServiceChanges: ConditionAndServiceChangeView
	currentFormValues: InventoryInformationFormValues
	inventoryAndIntakeChanges: InventoryAndIntakeChangeView
	isConditionValid: boolean
	isElectric: boolean
	modelYear: number | undefined
	onSave: (values: InventoryInformationChange) => Promise<void>
	pricingChanges: { pricing?: Partial<BasicPricingValues> } // just vatDeductible is relevant
	showUpdatePriceMessage: boolean
}

type Props = ModalProps<InventoryAndIntakeUpdateModalProps>

export const InventoryAndIntakeUpdateModal = ({ id, modalProps }: Props) => {
	const {
		conditionAndServiceChanges,
		currentFormValues,
		inventoryAndIntakeChanges,
		isConditionValid,
		isElectric,
		modelYear,
		onSave,
		pricingChanges,
		showUpdatePriceMessage,
	} = modalProps
	const [isSubmitting, setIsSubmitting] = useState(false)

	const { hideModal } = useContext(ModalContext)
	const hide = useCallback(() => hideModal(id), [hideModal, id])

	const form = useForm<InventoryInformationFormValues>({
		defaultValues: currentFormValues,
		mode: "onSubmit",
	})
	const { handleSubmit, watch } = form
	// eslint-disable-next-line react-hooks/incompatible-library
	const values = watch()

	const onSubmit = useCallback(() => {
		const patch = mapToInventoryInformationChange(values)
		if (patch) {
			setIsSubmitting(true)
			onSave(patch)
				.then(hide)
				.finally(() => setIsSubmitting(false))
		} else {
			hide()
		}
	}, [values, onSave, hide])

	return (
		<Modal id={id} disableBackdropClick data-testid="inventory-and-intake_update-modal">
			<ContentWrapper>
				<StyledHeading size="md">
					<FormattedMessage {...messages.changesHeadline} />
				</StyledHeading>
				<InventoryAndIntakeChanges view={inventoryAndIntakeChanges} />
				<ConditionAndServiceChanges view={conditionAndServiceChanges} />
				<PricingChanges view={pricingChanges} />

				<FormProvider {...form}>
					{(conditionAndServiceChanges ?? !isConditionValid) && (
						<Spacing>
							<StyledHeading size="md">
								<FormattedMessage {...messages.conditionHeadline} />
							</StyledHeading>
							<ConditionChangeForm
								values={currentFormValues}
								isElectric={isElectric}
								changes={conditionAndServiceChanges}
								modelYear={modelYear}
							/>
						</Spacing>
					)}
					{showUpdatePriceMessage && (
						<Spacing>
							<StyledHeading size="md">
								<FormattedMessage {...messages.listingHeadline} />
							</StyledHeading>
							<ListingChangeForm />
						</Spacing>
					)}
				</FormProvider>

				<ButtonGroup>
					<PButton loading={isSubmitting} onClick={handleSubmit(onSubmit)}>
						<FormattedMessage {...messages.saveChanges} />
					</PButton>
					<PButton type="button" variant="secondary" disabled={isSubmitting} onClick={hide}>
						<FormattedMessage {...formMessages.cancel} />
					</PButton>
				</ButtonGroup>
			</ContentWrapper>
		</Modal>
	)
}

const StyledHeading = styled(PHeading)`
	border-bottom: 1px solid ${colorContrastLow};
`

const ContentWrapper = styled.div`
	max-width: 100%;
	min-width: 40vw;
`

const Spacing = styled.div`
	margin-top: ${spacingStaticLg};
`
